import {
  Nido,
  NidoSingle,
  NidoDouble,
  NidoTriple,
} from '~/service/experience/world/nido/Nido'
import type { NidoPredefinedSet } from '~/types/nido/predefinedSet'
import {
  NidoCageType,
  NidoCode,
  NidoId,
  NidoType,
} from '~/service/experience/world/nido/enum'
import {
  NidoHighBackrest,
  NidoTexturePart,
  NidoLowBackrest,
} from '~/service/experience/world/nido/parts/NidoTexturePart'
import { useSurfaces } from '~/stores/surfaces'
import type { NidoCage } from '~/service/experience/world/nido/parts/NidoCage'

export function usePredefinedSet(nidos: Nido[]) {
  const surfacesStore = useSurfaces()

  const predefinedSet = computed(() => {
    return getPredefinedSet(nidos)
  })

  function checkLowBackrest(
    lowBackrest: NidoLowBackrest | undefined,
    cornerExpected = true
  ): boolean {
    if (lowBackrest === undefined) {
      return false
    }
    return (
      lowBackrest.code ===
      (cornerExpected ? NidoCode.LOW_BACKREST_CORNER : NidoCode.LOW_BACKREST)
    )
  }

  function checkHighBackrest(
    highBackrest: NidoHighBackrest | undefined,
    cornerExpected = true
  ): boolean {
    if (highBackrest === undefined) {
      return false
    }
    return (
      highBackrest.code ===
      (cornerExpected ? NidoCode.HIGH_BACKREST_CORNER : NidoCode.HIGH_BACKREST)
    )
  }

  function getPartsTextureIds(...nidoParts: (NidoTexturePart | undefined)[]) {
    return nidoParts
      .map((part: NidoTexturePart | undefined) => part?.textureId)
      .filter((textureId) => textureId !== null && textureId !== undefined)
  }

  function checkSurfaceCategory(...textureIds: number[]) {
    if (textureIds.length === 0) {
      return true
    }
    const firstSurfaceCategory = surfacesStore.getSurfaceCategory(textureIds[0])
    for (let i = 1; i < textureIds.length; i++) {
      if (
        surfacesStore.getSurfaceCategory(textureIds[i]) !== firstSurfaceCategory
      ) {
        return false
      }
    }
    return true
  }

  function getPredefinedSet(nidos: Nido[]): NidoPredefinedSet | null {
    if (nidos.length !== 1) {
      return null
    }

    const nido = nidos[0]

    switch (true) {
      case nido instanceof NidoSingle:
      case nido.type === NidoType.SINGLE: {
        const lowBackrest = nido.parts.center?.lowBackrest
        if (checkLowBackrest(lowBackrest)) {
          if (checkHighBackrest(lowBackrest?.highBackrest)) {
            switch (nido.parts.bottom.material) {
              case NidoCageType.METAL:
                return {
                  id: NidoId.SINGLE_PREDEFINED_HIGH,
                  nidoCode: NidoCode.SINGLE_PREDEFINED_HIGH,
                } as NidoPredefinedSet
              case NidoCageType.WOOD:
                return {
                  id: NidoId.SINGLE_PREDEFINED_HIGH_WOOD,
                  nidoCode: NidoCode.SINGLE_PREDEFINED_HIGH_WOOD,
                } as NidoPredefinedSet
            }
          }
          switch (nido.parts.bottom.material) {
            case NidoCageType.METAL:
              return {
                id: NidoId.SINGLE_PREDEFINED_LOW,
                nidoCode: NidoCode.SINGLE_PREDEFINED_LOW,
              } as NidoPredefinedSet
            case NidoCageType.WOOD:
              return {
                id: NidoId.SINGLE_PREDEFINED_LOW_WOOD,
                nidoCode: NidoCode.SINGLE_PREDEFINED_LOW_WOOD,
              } as NidoPredefinedSet
          }
        }
        return null
      }
      case nido instanceof NidoDouble:
      case nido.type === NidoType.DOUBLE: {
        const leftLowBackrest = nido.parts.left?.lowBackrest
        const rightLowBackrest = nido.parts.right?.lowBackrest
        const textureIds = getPartsTextureIds(
          nido.parts.left,
          nido.parts.right,
          leftLowBackrest,
          rightLowBackrest
        )
        const highTextureIds = getPartsTextureIds(
          leftLowBackrest?.highBackrest,
          rightLowBackrest?.highBackrest
        )
        if (
          checkLowBackrest(leftLowBackrest) &&
          checkLowBackrest(rightLowBackrest)
        ) {
          const hasLeftHighBackrest = checkHighBackrest(
            leftLowBackrest?.highBackrest
          )
          const hasRightHighBackrest = checkHighBackrest(
            rightLowBackrest?.highBackrest
          )
          if (
            hasLeftHighBackrest &&
            hasRightHighBackrest &&
            checkSurfaceCategory(...textureIds, ...highTextureIds)
          ) {
            switch (nido.parts.bottom.material) {
              case NidoCageType.METAL:
                return {
                  id: NidoId.DOUBLE_PREDEFINED_HIGH,
                  nidoCode: NidoCode.DOUBLE_PREDEFINED_HIGH,
                } as NidoPredefinedSet
              case NidoCageType.WOOD:
                return {
                  id: NidoId.DOUBLE_PREDEFINED_HIGH_WOOD,
                  nidoCode: NidoCode.DOUBLE_PREDEFINED_HIGH_WOOD,
                } as NidoPredefinedSet
            }
          } else if (
            !hasLeftHighBackrest &&
            !hasRightHighBackrest &&
            checkSurfaceCategory(...textureIds)
          ) {
            switch (nido.parts.bottom.material) {
              case NidoCageType.METAL:
                return {
                  id: NidoId.DOUBLE_PREDEFINED_LOW,
                  nidoCode: NidoCode.DOUBLE_PREDEFINED_LOW,
                } as NidoPredefinedSet
              case NidoCageType.WOOD:
                return {
                  id: NidoId.DOUBLE_PREDEFINED_LOW_WOOD,
                  nidoCode: NidoCode.DOUBLE_PREDEFINED_LOW_WOOD,
                } as NidoPredefinedSet
            }
          }
        }
        return null
      }
      case nido instanceof NidoTriple:
      case nido.type === NidoType.TRIPLE: {
        const leftLowBackrest = nido.parts.left?.lowBackrest
        const centerLowBackrest = nido.parts.center?.lowBackrest
        const rightLowBackrest = nido.parts.right?.lowBackrest
        const textureIds = getPartsTextureIds(
          nido.parts.left,
          nido.parts.center,
          nido.parts.right,
          leftLowBackrest,
          centerLowBackrest,
          rightLowBackrest
        )
        const highTextureIds = getPartsTextureIds(
          leftLowBackrest?.highBackrest,
          centerLowBackrest?.highBackrest,
          rightLowBackrest?.highBackrest
        )
        if (
          checkLowBackrest(leftLowBackrest) &&
          checkLowBackrest(centerLowBackrest, false) &&
          checkLowBackrest(rightLowBackrest)
        ) {
          const hasLeftHighBackrest = checkHighBackrest(
            leftLowBackrest?.highBackrest
          )
          const hasCenterHighBackrest = checkHighBackrest(
            centerLowBackrest?.highBackrest,
            false
          )
          const hasRightHighBackrest = checkHighBackrest(
            rightLowBackrest?.highBackrest
          )
          if (
            hasLeftHighBackrest &&
            hasCenterHighBackrest &&
            hasRightHighBackrest &&
            checkSurfaceCategory(...textureIds, ...highTextureIds)
          ) {
            switch (nido.parts.bottom.material) {
              case NidoCageType.METAL:
                return {
                  id: NidoId.TRIPLE_PREDEFINED_HIGH,
                  nidoCode: NidoCode.TRIPLE_PREDEFINED_HIGH,
                } as NidoPredefinedSet
              case NidoCageType.WOOD:
                return {
                  id: NidoId.TRIPLE_PREDEFINED_HIGH_WOOD,
                  nidoCode: NidoCode.TRIPLE_PREDEFINED_HIGH_WOOD,
                } as NidoPredefinedSet
            }
          } else if (
            !hasLeftHighBackrest &&
            !hasCenterHighBackrest &&
            !hasRightHighBackrest &&
            checkSurfaceCategory(...textureIds)
          ) {
            switch (nido.parts.bottom.material) {
              case NidoCageType.METAL:
                return {
                  id: NidoId.TRIPLE_PREDEFINED_LOW,
                  nidoCode: NidoCode.TRIPLE_PREDEFINED_LOW,
                } as NidoPredefinedSet
              case NidoCageType.WOOD:
                return {
                  id: NidoId.TRIPLE_PREDEFINED_LOW_WOOD,
                  nidoCode: NidoCode.TRIPLE_PREDEFINED_LOW_WOOD,
                } as NidoPredefinedSet
            }
          }
        }
        return null
      }
      default:
        // corner
        return null
    }
  }

  return { predefinedSet }
}
